import { Link } from 'react-router-dom';
import { BedDouble, Bath, Ruler, MapPin } from 'lucide-react';
import {
  getPropertyPrimaryPrice,
  getPropertyPriceSnapshot,
  getPropertyLocation,
  getPropertyTitle,
  getPropertyCardImage,
  getPropertyArea,
  getPropertyRooms,
  getPropertyBathrooms,
} from '../../utils/propertySelectors';

export default function PropertyCard({ property, lang = 'En' }) {
  const price = getPropertyPrimaryPrice(property);
  const snapshot = getPropertyPriceSnapshot(property);
  const title = getPropertyTitle(property);
  const location = getPropertyLocation(property);
  const area = getPropertyArea(property);
  const rooms = getPropertyRooms(property);
  const bathrooms = getPropertyBathrooms(property);

  const cover = getPropertyCardImage(property) || '';
  const isVideoCover = /\.(mp4|webm|ogg|mov)(\?|$)/i.test(cover);

  const href = property?.slug ? `/portfolio/${property.slug}` : `/portfolio/${property?._id}`;

  return (
    <Link
      to={href}
      className="group block overflow-hidden rounded-none border border-[#ece7df] bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#f5f1ea]">
        {cover ? (
          isVideoCover ? (
            <video
              src={`${cover}#t=0.1`}
              preload="metadata"
              muted
              playsInline
              className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
            />
          ) : (
            <img
              src={cover}
              alt={title}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
            />
          )
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-[#8f887c]">
            No image
          </div>
        )}
      </div>

      <div className="space-y-4 p-5">
        <div className="space-y-2">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#9c9486] font-semibold">
            {location || 'Location unavailable'}
          </p>
          <h3 className="text-lg font-semibold leading-tight text-black">
            {title}
          </h3>
        </div>

        <div>
          <div className="text-xl font-semibold text-black">
            {price?.amount
              ? `${Number(price.amount).toLocaleString()} ${price.currency}`
              : (lang === 'Fr' ? 'Sur demande' : 'On Request')}
          </div>

          {snapshot && (
            <p className="mt-2 text-[11px] uppercase tracking-[0.16em] text-[#8b8478] leading-relaxed">
              {snapshot.usd ? `${Number(snapshot.usd).toLocaleString()} USD` : ''}
              {snapshot.usd && snapshot.eur ? ' · ' : ''}
              {snapshot.eur ? `${Number(snapshot.eur).toLocaleString()} EUR` : ''}
              {(snapshot.usd || snapshot.eur) && snapshot.btc ? ' · ' : ''}
              {snapshot.btc ? `₿ ${Number(snapshot.btc).toLocaleString(undefined, { maximumFractionDigits: 4 })}` : ''}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-4 border-t border-[#f1ece4] pt-4 text-sm text-[#4d473f]">
          {rooms ? (
            <span className="inline-flex items-center gap-2">
              <BedDouble size={16} className="text-[#9c9486]" />
              {rooms}
            </span>
          ) : null}

          {bathrooms ? (
            <span className="inline-flex items-center gap-2">
              <Bath size={16} className="text-[#9c9486]" />
              {bathrooms}
            </span>
          ) : null}

          {area ? (
            <span className="inline-flex items-center gap-2">
              <Ruler size={16} className="text-[#9c9486]" />
              {area} sqft
            </span>
          ) : null}

          {location ? (
            <span className="inline-flex items-center gap-2">
              <MapPin size={16} className="text-[#9c9486]" />
              {location.split(',')[0]}
            </span>
          ) : null}
        </div>
      </div>
    </Link>
  );
}
